import { useEffect, useState } from "react";
import { jt, t } from "ttag";

import { LiveRegionAnnouncer } from "metabase/common/components/LiveRegionAnnouncer";
import { duration } from "metabase/utils/formatting";
import type { CardSlownessStatus } from "metabase/visualizations/types";

import {
  Duration,
  Root,
  ShortMessage,
  SlowQueryMessageContainer,
  StyledLoadingSpinner,
} from "./LoadingView.styled";

export interface LoadingViewProps {
  isSlow: CardSlownessStatus | undefined;
  expectedDuration?: number;
}

// See the matching hook in LoadingAndErrorWrapper.tsx for why this defers
// via a plain effect (not requestAnimationFrame) and why the role/aria-live
// attributes belong on the *already-visible* ShortMessage rather than on a
// separate hidden node: this text is already shown to sighted users, so
// duplicating it into a hidden announcer would make text queries ambiguous.
function useDeferredText(text: string): string {
  const [deferred, setDeferred] = useState("");

  useEffect(() => {
    setDeferred(text);
  }, [text]);

  return deferred;
}

function SlowQueryView({ expectedDuration, isSlow }: LoadingViewProps) {
  const deferredMessage = useDeferredText(t`Still Waiting…`);

  return (
    <SlowQueryMessageContainer role="status" aria-live="polite">
      <ShortMessage>{deferredMessage}</ShortMessage>
      {isSlow === "usually-slow" ? (
        <div>
          {jt`This usually takes an average of ${(
            <Duration key="duration">
              {duration(expectedDuration ?? 0)}
            </Duration>
          )}, but is currently taking longer.`}
        </div>
      ) : (
        <div>
          {t`This usually loads immediately, but is currently taking longer.`}
        </div>
      )}
    </SlowQueryMessageContainer>
  );
}

function LoadingView({ expectedDuration, isSlow }: LoadingViewProps) {
  return (
    <Root>
      {isSlow ? (
        <SlowQueryView expectedDuration={expectedDuration} isSlow={isSlow} />
      ) : (
        <>
          {/* No visible text accompanies the bare spinner, so a hidden
              announcer (rather than the shared-node pattern above) is
              safe here: there's no visible copy for it to duplicate. */}
          <LiveRegionAnnouncer message={t`Loading…`} />
          <StyledLoadingSpinner />
        </>
      )}
    </Root>
  );
}

// eslint-disable-next-line import/no-default-export -- deprecated usage
export default LoadingView;
